import type { KeycapId } from "@/components/keycap/keycapCatalog";

/**
 * Availability status vocabulary used across the product/solutions pages. Never invent new
 * values without also updating StatusBadge's icon/colour map and CSS.
 *
 * "live" added 2026-09-13: Public Release 0.1 has been live since 2026-08-07 with real
 * self-serve signup, Stripe billing, and scoped cloud execution -- "pilot" (internal,
 * invitation-only) and "live" (public, self-service) are genuinely different claims and must
 * not be collapsed into one status. Never mark something "live" here unless it is reachable by
 * a real, unauthenticated visitor today without contacting Devory first.
 */
export type FeatureStatus = "live" | "pilot" | "in-development" | "planned";

export const statusLabels: Record<FeatureStatus, string> = {
  live: "Live",
  pilot: "Available in pilot",
  "in-development": "In active development",
  planned: "Planned",
};

/** How /product groups capabilities — see the 2026-07-18 brand redesign pass. */
export type CapabilityGroup = "build" | "execute" | "govern" | "extend";

export const capabilityGroupLabels: Record<CapabilityGroup, string> = {
  build: "Build",
  execute: "Execute",
  govern: "Govern",
  extend: "Extend",
};

export interface Capability {
  id: string;
  title: string;
  description: string;
  status: FeatureStatus;
  group: CapabilityGroup;
  /** The key that represents this capability wherever it is shown. */
  keycap: KeycapId;
}

export interface FaqItem {
  question: string;
  answer: string;
}

/**
 * The five connected product surfaces that make up Devory — see /product.
 * Never present these as independently completed commercial SKUs; each
 * `status` is the same honest `FeatureStatus` vocabulary used everywhere
 * else on the site.
 */
export interface Product {
  id: string;
  name: string;
  tagline: string;
  status: FeatureStatus;
  capabilities: string[];
  link: { label: string; to: string };
  keycap: KeycapId;
}

/**
 * Marketplace object types Devory's own filesystem-backed component
 * catalogue actually defines (`Components/README.md`, ADR-0065) — never
 * invent a category the domain model doesn't have.
 */
export type MarketplaceCategory =
  "component" | "collection" | "workflow" | "agents-and-integrations" | "qualification-profile";

export const marketplaceCategoryLabels: Record<MarketplaceCategory, string> = {
  component: "Components",
  collection: "Collections",
  workflow: "Workflows",
  "agents-and-integrations": "Agents and integrations",
  "qualification-profile": "Qualification profiles",
};

/**
 * Acquisition status is deliberately separate from engineering `status`
 * (`FeatureStatus`) — a listing can be technically implemented and still
 * not publicly installable. Never invent a value outside this fixed set.
 */
export type MarketplaceAvailability =
  | "available-in-pilot"
  | "catalogue-preview"
  | "in-qualification"
  | "in-active-development"
  | "planned"
  | "not-yet-installable";

export const marketplaceAvailabilityLabels: Record<MarketplaceAvailability, string> = {
  "available-in-pilot": "Available in pilot",
  "catalogue-preview": "Catalogue preview",
  "in-qualification": "In qualification",
  "in-active-development": "In active development",
  planned: "Planned",
  "not-yet-installable": "Not yet installable",
};

export interface MarketplaceListing {
  id: string;
  name: string;
  category: MarketplaceCategory;
  description: string;
  publisher: string;
  firstParty: boolean;
  compatibility: string;
  status: FeatureStatus;
  availability: MarketplaceAvailability;
  installable: boolean;
  /** Real, verified version identifier — omit rather than invent one. */
  version?: string;
  /** Real, verified qualification state in plain words — omit rather than invent one. */
  qualification?: string;
  detailPath?: string;
  keycap: KeycapId;
}
